import { msg } from "@omegagrid/localize";
import { ColorTypes, dates } from "@omegagrid/core";
import { TreeSource } from "@omegagrid/tree";
import { CellModel, CellSource, ColumnSource, RowModel, RowSource } from "@omegagrid/grid";
import rp, { CellState, GridEditorAdapter, GridEditorOptions, RowData, toIsoDate } from "@reporting/rp-app-host";
import { ETLJob, JobParam, JobSchedule, OCCURRENCE_TYPES, WEEK_OCCURRENCE_TYPES } from "@reporting/rp-types/jobs";
import { JOB_PARAMS } from "../types";
import { ActionEvent } from "@omegagrid/core/dist/common/actions";
import { AccountingRights } from "@reporting/rp-types/core";

type ParamColumn = {
	name: keyof JobParam;
	idThread: number;
	description: string;
};

export class SchedulesGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private paramCols: ParamColumn[];
	private jobParam: JobParam;
	private readOnly: boolean = false;

	constructor(
		private jobName: string,
	) { super() }

	async afterLoad(): Promise<void> {
		// load job params for default values when adding new schedule
		if (!this.jobParam) this.jobParam = await rp.ms('jobs').get<JobParam>('params', { job_name: this.jobName });
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 1,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}],
			filter: false,
			hidden: this.readOnly,
		}, {
			data: 'name',
			header: [{ label: msg('Schedule'), cs: 5 }, { label: msg('Name') }],
			t: 't',
			w: 300,
		}, {
			data: 'enabled',
			header: [null, { label: msg('Enabled') }],
			t: 'b',
			w: 80,
		}, {
			data: 'freq_type',
			header: [null, { label: msg('Occurs') }],
			t: 's',
			w: 100,
			content: Object.entries(OCCURRENCE_TYPES).map(([k, o]) => ({ k: o.id, v: msg(o.text) }))
		}, {
			data: '_day',
			header: [null, { label: msg('Day') }],
			t: 't',
			w: 100,
		}, {
			data: '_time',
			header: [null, { label: msg('Time') }],
			t: 't', // TODO: change to 'tm' when time type supported
			w: 100,
		}];

		this.paramCols = await this.getValidParameterColumns();
		for (let i = 0; i < this.paramCols.length; i++) {
			const pCol = this.paramCols[i];
			this._columns.push({
				data: pCol.name,
				header: [
					i === 0 ? { label: msg('Parameters'), cs: this.paramCols.length } : null,
					{ label: pCol.description }
				],
				t: 's',
				w: 200,
				// adapter: new ParamTreeAdapter(rp.ms('etl'), pCol.idThread), // missing display value
				content: await this.getEtlList(pCol.idThread)
			});
		}

		this._columns.push({
			data: 'schedule_id',
			t: 'n',
			e: false,
			hidden: true,
		});

		return this._columns;
	}

	private async getEtlList(idThread: number): Promise<TreeSource> {
		const result = await rp.ms('etl').get<TreeSource[]>(`/exec/${idThread}`,
			{ scope: 1 },
			{ headers: { accept: 'application/json' } }
		);
		return result && result.length ? result[0] : [];
	}

	private async getValidParameterColumns(): Promise<ParamColumn[]> {
		const job = await rp.ms('jobs').get<ETLJob>('jobs', { job_name: this.jobName });
		return JOB_PARAMS.map(p => {
			const idThread = job[`id_thread_${p.name}` as keyof ETLJob] as number;
			if (!idThread) return null;
			
			let description = (job[`description_${p.name}` as keyof ETLJob] as string);
			if (!description) description = p.name === 'value_i' ? msg('Year') : p.name

			return {
				name: p.name,
				idThread,
				description
			};
		}).filter((col): col is ParamColumn => col !== null);
	}

	async getRows(): Promise<RowSource[]> {
		const schedules = await rp.ms('jobs').get<JobSchedule[]>('schedules', {
			job_name: this.jobName,
			includeParams: true
		});
		return schedules.map(data => ({ data: {
			name: { e: false, v: data.name },
			enabled: data.enabled,
			freq_type: data.freq_type,
			_day: this.createDayCell(data),
			_time: this.formatTime(data.active_start_time),
			...data.params,
			schedule_id: data.schedule_id,
		}}));
	}

	createDayCell(schedule: JobSchedule): Partial<CellSource> {
		switch (schedule.freq_type) {
			case OCCURRENCE_TYPES.once.id:
				return {
					t: 'd',
					z: dates.getDateFormat(), // a day only: the default date format of the grid carries the time
					v: this.formatDate(schedule.active_start_date) || new Date().toISOString().slice(0, 10),
					e: !this.readOnly
				};
			case OCCURRENCE_TYPES.weekly.id:
				return {
					t: 's',
					content: Object.entries(WEEK_OCCURRENCE_TYPES).map(([k, o]) => ({ k: o.id, v: msg(o.text) })),
					v: schedule.freq_interval || 1,
					e: !this.readOnly
				};
			case OCCURRENCE_TYPES.monthly.id:
				return {
					t: 's',
					content: Array.from({ length: 28 }, (_, i) => i + 1).map(d => ({ k: d, v: String(d) })),
					v: schedule.freq_interval || 1,
					e: !this.readOnly
				};
			default:
				return { t: 't', e: false, v: null };
		}
	}

	// time stored as HHMMSS
	formatTime(t: number): string|null {
		if (t === undefined || t === null) return null;
		const tstr = String(t).padStart(6, '0').slice(-6);
		const hh = tstr.slice(0, 2);
		const mm = tstr.slice(2, 4);
		return `${hh}:${mm}`;
	}

	// date stored as YYYYMMDD
	formatDate(d: number): string|null {
		if (d === undefined || d === null) return null;
		const dstr = String(d).padStart(8, '0').slice(-8);
		const yyyy = dstr.slice(0, 4);
		const mm = dstr.slice(4, 6);
		const dd = dstr.slice(6, 8);
		return `${yyyy}-${mm}-${dd}`;
	}

	async save(rowData: RowData[]) {
		const schedules: JobSchedule & { _state: CellState }[] = rowData.map(row => ({
			_state: row.state,
			job_name: this.jobName,
			schedule_id: Number(row.data['schedule_id']),
			name: String(row.data['name']),
			enabled: Boolean(row.data['enabled']),
			freq_type: Number(row.data['freq_type']),
			active_start_date: this.parseDayValueAsStartDate(row.data['_day'], Number(row.data['freq_type'])),
			freq_interval: this.parseDayValueAsFreqInterval(row.data['_day'], Number(row.data['freq_type'])),
			active_start_time: this.convertTimeToNumeric(String(row.data['_time'])),
			params: this.extractParams(row.data),
		}));
		await rp.ms('jobs').post('schedules', schedules);
	}

	// the day cell holds a serial date of the grid (or the ISO string it was loaded with); stored as YYYYMMDD
	parseDayValueAsStartDate(day: unknown, freqType: number): number|null {
		if (freqType !== OCCURRENCE_TYPES.once.id) return null;
		const iso = toIsoDate(day as Date | string | number);
		return iso ? parseInt(iso.replace(/-/g, ''), 10) || null : null;
	}

	parseDayValueAsFreqInterval(day: unknown, freqType: number): number|null {
		if (freqType !== OCCURRENCE_TYPES.weekly.id && freqType !== OCCURRENCE_TYPES.monthly.id) return null;
		const num = Number(day);
		return isNaN(num) ? null : num;
	}

	convertTimeToNumeric(time: string): number|null { // TODO: adjust when time type supported
		if (!time) return null;
		const parts = time.split(':');
		if (parts.length < 2) return null;
		const hh = parts[0].padStart(2, '0');
		const mm = parts[1].padStart(2, '0');
		return parseInt(`${hh}${mm}00`);
	}

	extractParams(rowData: Record<string, unknown>): Partial<JobParam> {
		if (!this.paramCols || !rowData) return {};
		return this.paramCols.reduce((acc, pCol) => {
			const val = rowData[pCol.name];
			if (val !== undefined) {
				// a cleared cell is null (or ''): send null so the backend stores NULL instead of 0 / "null"
				(acc as any)[pCol.name] = val === null || val === '' ? null : String(val);
			}
			return acc;
		}, {} as Partial<JobParam>);
	}

	getOptions(): Partial<GridEditorOptions> {
		this.readOnly = !rp.userProvider.hasRight(AccountingRights.JobsDefine);
		return this.createOptions({
			editable: !this.readOnly,
			colLetters: false,
			rowNumbers: true,
			columnSorting: true,
			filters: true,
			refreshAfterSave: true,
			pageSize: null,
			saveMode: 'edited_rows',
			editing: { dateCalendarEditor: true },
			toolbar: {
				new: { enabled: !this.readOnly },
				save: { enabled: !this.readOnly },
				discard: { enabled: !this.readOnly },
			}
		});
	}

	onAction(action: string, cell: CellModel, event?: ActionEvent<null>) {
		if (action === 'addRow') {
			cell.row.data = {
				name: `${this.jobName}_SCHEDULE_${this.editor.grid.model.rows.length}`,
				enabled: true,
				freq_type: OCCURRENCE_TYPES.once.id,
				_day: new Date().toISOString().slice(0, 10),
				_time: '00:00',
				...this.extractParams(this.jobParam), // default params from job loaded in afterLoad()
			}
		}
	}

	reloadDayCells(rows: RowModel[]) {
		const dayColumnIndex = this.editor.grid.model.columns.find(c => c.data === '_day').index;
		for (const row of rows) {
			const cell = this.editor.grid.model.getCellByCoords(row.index, dayColumnIndex);
			cell.update(this.createDayCell({ freq_type: row.data.freq_type } as JobSchedule));
			this.editor.grid.rendering.updateCell(cell);
		}
	}

}
